import {
  IconButton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { LinkButton } from "components/button";
import { CloseIcon, InfoIcon } from "components/icons";
import type { ReactNode } from "react";

interface StripProps {
  variant: "success" | "error" | "warning" | "info";
  label: ReactNode;
  onClick?: () => void;
  onClose?: () => void;
  closeable?: boolean;
  buttonText?: string;
  sx?: SxProps<Theme>;
  leadIconsx?: SxProps<Theme>;
  rightAction?: React.ReactNode;
  labelStyle?: SxProps<Theme>;
  children?: ReactNode;
}

export const Strip = ({
  variant = "info",
  label,
  onClick,
  onClose,
  closeable = true,
  buttonText,
  rightAction,
  leadIconsx,
  sx,
  labelStyle,
  children,
}: StripProps) => {
  const theme = useTheme();
  const variantColors = {
    success: {
      backgroundColor: theme.palette.primitive.system.green[100],
      color: theme.palette.primitive.system.green[800],
    },
    error: {
      backgroundColor: theme.palette.primitive.system.red[100],
      color: theme.palette.primitive.system.red[800],
    },
    warning: {
      backgroundColor: theme.palette.primitive.system.orange[100],
      color: theme.palette.primitive.system.orange[800],
    },
    info: {
      backgroundColor: theme.palette.primitive.system.blue[100],
      color: theme.palette.primitive.system.blue[800],
    },
  };

  const labelNode = (
    <Typography
      variant="body-small"
      sx={{
        ...(labelStyle ? labelStyle : {}),
        color: "inherit",
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        backgroundColor: variantColors[variant].backgroundColor,
        color: variantColors[variant].color,
        alignItems: "center",
        justifyContent: "space-between",
        p: "4px 32px",
        gap: "16px",
        ...(sx ? sx : {}),
      }}
    >
      <Stack
        direction="row"
        gap="8px"
        alignItems={children ? "flex-start" : "center"}
        flex={children ? 1 : undefined}
      >
        <InfoIcon
          sx={{
            width: "20px",
            height: "20px",
            ...(leadIconsx ? leadIconsx : {}),
          }}
          htmlColor={"inherit"}
        />
        {children ? (
          <Stack flex={1} minWidth={0}>
            {labelNode}
            {children}
          </Stack>
        ) : (
          labelNode
        )}
        {(buttonText || onClick) && (
          <LinkButton
            size="small"
            sx={{
              p: "0px",
              m: "0px",
              minWidth: "0px",
              ml: "16px",
            }}
            onClick={() => onClick?.()}
            label={buttonText ? buttonText : "Click here"}
          />
        )}
      </Stack>
      {rightAction ||
        (closeable && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ))}
    </Stack>
  );
};
